// VersionHistoryPanel — collapsed disclosure ("Lease Versions (N)") that
// expands to a vertical list of generated PDFs (§1.2 / §1.10).
//
// Built on native <details> + a DS .card wrapper. The disclosure form (rather
// than a side drawer) is preserved because the DS does not ship a drawer
// primitive and the host page expects the panel inline with the lease editor.

import { useState } from 'react';
import { toast } from '@/components/Toast';
import * as drafts from '@/lib/leaseDraftsClient';
import type { LeasePdfVersionRecord } from '@/lib/leaseDraftsClient';

interface VersionHistoryPanelProps {
  groupId: string;
  versions: LeasePdfVersionRecord[];
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return iso;
  }
}

export function VersionHistoryPanel({
  groupId,
  versions,
}: VersionHistoryPanelProps): React.JSX.Element {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const downloadVersion = async (versionId: string) => {
    setDownloadingId(versionId);
    try {
      const { downloadUrl } = await drafts.getVersion(groupId, versionId);
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.warn('VersionHistoryPanel.download.failed', err);
      toast.error('Could not download this version. Try refreshing the page.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <details
      className="card"
      style={{
        margin: 'var(--space-5) var(--space-5) var(--space-7)',
        padding: 'var(--space-3) var(--space-4)',
      }}
    >
      <summary
        style={{
          cursor: 'pointer',
          fontWeight: 'var(--weight-medium)',
          color: 'var(--fg)',
          fontSize: 'var(--ui-text)',
        }}
      >
        Lease Versions ({versions.length})
      </summary>
      {versions.length === 0 ? (
        <div
          style={{
            color: 'var(--fg-muted)',
            padding: 'var(--space-3) 0',
            fontSize: 'var(--text-sm)',
          }}
        >
          No versions generated yet.
        </div>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 'var(--space-3) 0 0',
          }}
        >
          {versions.map((v) => (
            <li
              key={v.versionId}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--space-3)',
                minHeight: 'var(--ui-row)',
                borderTop: '1px solid var(--border)',
              }}
            >
              <span style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>
                {formatDateTime(v.generatedAt)} • {v.generatedByInitials || '—'}
              </span>
              <button
                type="button"
                className="btn"
                data-variant="link"
                data-size="sm"
                onClick={() => void downloadVersion(v.versionId)}
                disabled={downloadingId === v.versionId}
              >
                {downloadingId === v.versionId ? 'Loading…' : 'Download PDF'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}
